import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HvGrid, ListValueProp } from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  useHvPagination,
  useHvTable,
} from "@hitachivantara/uikit-react-lab";
import { useFormikContext } from "formik";
import { textColumn, dropdownColumn } from "components/common/SimpleTable";
import { useEventTypes, useIncidentTypes } from "lib/api";
import { useUniqueId } from "lib/utils/ids";
import MapPropertiesTable from "./MapPropertiesTable";
import { MapPropertiesDataRow } from "./MapPropertiesTable/MapPropertiesTable";
import { usePrevious } from "./usePrevious";
import { createMapPropertiesTableData, createPayload } from "./utils";

export interface MapPropertiesProps {
  selectedEventTypeId?: string;
}

const MapProperties: React.FC<MapPropertiesProps> = ({
  selectedEventTypeId,
}) => {
  const { t } = useTranslation("incidentDialog");
  const [tableData, setTableData] = useState<MapPropertiesDataRow[] | null>(
    null
  );
  const elementId = useUniqueId(selectedEventTypeId, "mapProperties");
  const { data: typeData } = useIncidentTypes(undefined, {
    revalidateOnFocus: false,
  });

  const { data: eventTypeValues } = useEventTypes({
    revalidateOnFocus: false,
  });

  const { values, touched, setFieldTouched, setFieldValue, setStatus } =
    useFormikContext<IncidentDialogFormData>();
  const previousType = usePrevious(values.type);

  const setValueOnForm = useCallback(
    (property: string, selection: unknown) => {
      setFieldValue(property, selection);
      setFieldTouched(property, true, false);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldTouched, setFieldValue, setStatus]
  );

  const onEventPropertySelect = useCallback(
    (id: string, value: ListValueProp) => {
      const newPayload = createPayload(values.mapProperties, id, value);
      setValueOnForm("mapProperties", newPayload);
    },
    [setValueOnForm, values.mapProperties]
  );

  const columns = useMemo(
    (): HvTableColumnConfig<MapPropertiesDataRow>[] => [
      textColumn({
        Header: t("columns.id") as string,
        accessor: "id",
        style: { width: "8%" },
      }),
      textColumn({
        Header: t("columns.incidentProperty") as string,
        accessor: "incidentProperty",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("columns.incidentPropertyType") as string,
        accessor: "incidentPropertyType",
        style: { width: "20%" },
      }),
      dropdownColumn(
        {
          Header: t("columns.entityProperty.header") as string,
          accessor: "entityProperty",
          style: { width: "20%" },
        },
        elementId,
        t("columns.entityProperty.placeholder"),
        t("columns.entityProperty.disabledPlaceholder"),
        onEventPropertySelect
      ),
      textColumn({
        Header: t("columns.entityPropertyType") as string,
        accessor: "entityPropertyType",
        style: { width: "20%" },
      }),
    ],
    [elementId, onEventPropertySelect, t]
  );

  useEffect(() => {
    if (touched.type && values.type && previousType !== values.type) {
      // properties should be clean whenever the user changes selected the incidentType
      setFieldValue("mapProperties", {}, false);
    }
  }, [values.type, touched.type, setFieldValue, previousType]);

  useEffect(() => {
    if (typeData && eventTypeValues && values.type) {
      const selectedIncidentType = typeData.data.find(
        (v) => v.pk_inc_type_id === values.type
      );

      const selectedEventType = eventTypeValues.data.find(
        (v) => v.id === selectedEventTypeId
      );
      setTableData(
        createMapPropertiesTableData(
          selectedIncidentType,
          selectedEventType,
          values.mapProperties
        )
      );
    } else {
      setTableData([]);
    }
  }, [
    typeData,
    eventTypeValues,
    values.type,
    selectedEventTypeId,
    values.mapProperties,
  ]);

  const instance = useHvTable<MapPropertiesDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: MapPropertiesDataRow): string => `${v.id}`, []),
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvPagination
  );

  return (
    <HvGrid item xs={12}>
      <MapPropertiesTable
        id={elementId}
        instance={instance}
        loading={tableData === []}
      />
    </HvGrid>
  );
};

export default MapProperties;
